import styled from '@emotion/styled'
export const PageContainer = styled.div`
  background: #ffffff;
  color: #1f2937;
  width: 100%;
  min-width: 0;
`

export const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    max-width: 1400px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 16px 12px;
    min-width: 0;
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

export const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ef4444;
`

export const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  max-width: 400px;
`

export const RetryButton = styled.button`
  padding: 12px 24px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }
`

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`

export const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
`

export const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? 'var(--portal-primary, #7c3aed)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--portal-primary, #7c3aed)' : '#6b7280')};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: -2px;
  white-space: nowrap;

  &:hover {
    color: var(--portal-primary, #7c3aed);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 14px;
  }
`

/** Sticky bar after ticket groups; pins to the bottom of the viewport while scrolling */
export const StickyBuyBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  justify-content: center;
  margin-top: 24px;
  margin-left: -12px;
  margin-right: -12px;
  width: calc(100% + 24px);
  padding: 16px 12px calc(16px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.06);

  @media (min-width: 769px) {
    margin-left: -40px;
    margin-right: -40px;
    width: calc(100% + 80px);
    padding-left: 40px;
    padding-right: 40px;
  }
`

export const BuyTicketsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  min-height: 44px;
  white-space: nowrap;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.3);
  }

  span {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    justify-content: center;
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 16px;
`

export const EmptyIcon = styled.div`
  font-size: 64px;
  color: #6b7280;
`

export const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

export const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  text-align: center;
  max-width: 400px;
  margin: 0;
  line-height: 1.5;
`

export const TicketsList = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: 769px) {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 480px), 1fr));
  }
`

export const TicketCard = styled.div`
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.15);
  }
`

export const DetailOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
`

export const DetailCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
  position: relative;
`

export const DetailCardCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`

export const DetailCardCloseButtonGreen = styled(DetailCardCloseButton)`
  background: rgba(22, 163, 74, 0.2);
  color: #166534;

  &:hover {
    background: rgba(22, 163, 74, 0.35);
    color: #14532d;
  }
`

export const DetailCardContent = styled.div`
  padding: 24px;
  padding-top: 48px;
`

export const DetailCardTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
`

export const DetailQRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 20px;
`

export const DetailQRWrapper = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;

  & svg {
    forced-color-adjust: none;
  }

  & img {
    forced-color-adjust: none;
  }
`

export const DetailCardRecentlyActivated = styled(DetailCard)`
  background: #dcfce7;
  border: 3px solid #16a34a;
`

export const LastActivatedSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 20px;
  margin-bottom: 20px;
  background: rgba(22, 163, 74, 0.1);
  border-radius: 12px;
  border: 2px solid #16a34a;
`

export const LastActivatedText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #166534;
  text-align: center;
`

export const LastActivatedOkLabel = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #16a34a;
  letter-spacing: 0.05em;
`

export const DetailQRSectionCompact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  margin-bottom: 16px;
`

export const DetailQRWrapperCompact = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  max-width: 120px;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;

  & svg {
    max-width: 100%;
    height: auto;
    forced-color-adjust: none;
  }

  & img {
    forced-color-adjust: none;
  }
`

export const DetailQRWrapperClickable = styled(DetailQRWrapper)`
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const DetailQRWrapperCompactClickable = styled(DetailQRWrapperCompact)`
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const DetailCloseButton = styled.button`
  width: 100%;
  margin-top: 16px;
  padding: 12px 24px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`

export const DetailViewReceiptLink = styled.a`
  display: block;
  width: 100%;
  padding: 12px 24px;
  margin-top: 12px;
  background: transparent;
  border: 1px solid var(--portal-primary, #7c3aed);
  border-radius: 8px;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &:hover {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.08);
    color: var(--portal-primary-dark, #6d28d9);
  }
`

export const TicketTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  flex: 1;
`

export const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  flex-shrink: 0;

  ${({ status }) => {
    switch (status) {
      case 'active':
      case 'deleted':
        return `
          background: rgba(76, 175, 80, 0.1);
          color: #4CAF50;
        `
      case 'pending_payment':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #f59e0b;
        `
      case 'expired':
      case 'usedUp':
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
      case 'refunded':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `
      default:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
    }
  }}
`

export const CardTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

export const StatusPill = styled(StatusBadge)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.75;
  }
`

export const SchoolSubtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.35;
`

export const CardHeroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  margin-top: 4px;
`

export const PassesHeroColumn = styled.div`
  flex: 1;
  min-width: 0;
`

export const PassesHeroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 12px;
  margin-bottom: 8px;
`

export const PassesBig = styled.span`
  font-size: 48px;
  font-weight: 800;
  line-height: 1;
  color: var(--portal-primary, #7c3aed);
  letter-spacing: -0.02em;
`

export const PassesLeftLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  max-width: 220px;
`

export const ProgressBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: var(--portal-primary-surface-strong, #ede9fe);
  overflow: hidden;
`

export const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--portal-primary, #7c3aed);
  transition: width 0.25s ease;
`

export const QrThumbColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (min-width: 400px) {
    margin-left: auto;
  }
`

export const QrThumbButton = styled.button`
  display: block;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid var(--portal-primary, #7c3aed);
    outline-offset: 2px;
  }
`

export const QrThumbInner = styled.div`
  background: #ffffff;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  line-height: 0;
  color-scheme: light only;
  isolation: isolate;
  forced-color-adjust: none;

  & img {
    display: block;
    forced-color-adjust: none;
  }
`

/** Holds the thumb at its final 84px while the QR PNG is generated. */
export const QrThumbPlaceholder = styled.div`
  width: 84px;
  height: 84px;
  background: #ffffff;
`

export const TapForQrButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  border-radius: 999px;
  background: var(--portal-primary-surface, #f5f3ff);
  color: var(--portal-primary, #7c3aed);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-surface-strong, #ede9fe);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--portal-primary-rgb, 124, 58, 237), 0.35);
  }
`

export const ValidUntilFooter = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`

export const DetailTopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`

export const DetailTopBarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

export const DetailSchoolSubtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
  text-align: right;
  max-width: 200px;

  @media (max-width: 480px) {
    text-align: left;
    max-width: none;
    width: 100%;
  }
`

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 8px;
  margin-top: 4px;
`

export const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
`

export const DetailRowLabel = styled.span`
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
`

export const DetailRowValue = styled.span`
  font-weight: 600;
  color: #111827;
  text-align: right;
`

export const DetailWalletSection = styled.div`
  margin-top: 20px;
`

export const DetailDetailsSection = styled.div`
  margin-top: 20px;
`

export const ExpiringSoonBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(251, 191, 36, 0.15);
  color: #d97706;
  flex-shrink: 0;
`

export const LastUsedSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`

export const LastUsedTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
`

export const LastUsedItem = styled.div`
  font-size: 13px;
  color: #374151;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-of-type {
    border-bottom: none;
  }
`

export const LastUsedItemLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

export const LastUsedSchool = styled.span`
  font-weight: 500;
  color: #111827;
`

export const ClipUsedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  background: rgba(251, 191, 36, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
`

export const LastUsedDateTime = styled.span`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
  display: block;
`
